import React from "react"
import { Modal, Button, Flex, Tag, Typography } from "antd"
import { EnvironmentOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { Trans, useTranslation } from "react-i18next"
import WarningOutlinedWithBg from "ui/WarningOutlinedWithBg"
import styles from "./index.module.css"

const { Text } = Typography

const ACTION_TYPES = {
  DEACTIVATE: "deactivate",
  DELETE: "delete"
}

const statusTagProps = {
  published: { color: "success", labelKey: "settings.status_published" },
  unpublished: { color: "default", labelKey: "settings.status_unpublished" },
  draft: { color: "warning", labelKey: "settings.status_draft" },
  archived: { color: "default", labelKey: "settings.status_archived" }
}

const AssociatedExperiencesConfirmModal = ({
  visible,
  onCancel,
  onConfirm,
  actionType,
  experiences = [],
  isCheckingAssociations = false,
  isConfirming = false,
  customFeeTitle = ""
}) => {
  const { t } = useTranslation()

  const isDelete = actionType === ACTION_TYPES.DELETE

  const title = isDelete ? t("settings.assoc_exp_delete_title") : t("settings.assoc_exp_deactivate_title")
  const confirmText = isDelete ? t("settings.action_delete") : t("settings.action_deactivate")
  const emptyStateText = isDelete ? t("settings.assoc_exp_empty_delete") : t("settings.assoc_exp_empty_deactivate")

  const renderExperiencesContent = () => {
    if (isCheckingAssociations) {
      return <div className={styles.loadingState}>{t("settings.assoc_exp_loading")}</div>
    }

    if (experiences.length === 0) {
      return <div className={styles.emptyState}>{emptyStateText}</div>
    }

    return (
      <div className={styles.experienceList}>
        {experiences.map((exp) => {
          const tagProps = statusTagProps[exp.status] || statusTagProps.unpublished
          return (
            <div key={exp.id} className={styles.experienceCard}>
              <Flex justify="space-between" align="center" gap={8}>
                <Text className={styles.experienceTitle}>{exp.title}</Text>
                <Tag color={tagProps.color}>{t(tagProps.labelKey)}</Tag>
              </Flex>
              {exp.address && (
                <Flex align="center" gap={4} className={styles.locationRow}>
                  <EnvironmentOutlined className={styles.locationIcon} />
                  <Text className={styles.locationText}>{exp.address}</Text>
                </Flex>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Modal open={visible} onCancel={onCancel} footer={null} centered width={520} maskClosable={false} className={styles.modal}>
      <div className={styles.modalContent}>
        <Flex justify="center" className={styles.iconWrapper}>
          <WarningOutlinedWithBg />
        </Flex>

        <h3 className={styles.modalTitle}>{title}</h3>

        <Text className={styles.irreversibleText}>
          <Trans i18nKey="settings.assoc_exp_irreversible_warning" values={{ title: customFeeTitle }} components={{ strong: <strong /> }} />
        </Text>

        <div className={styles.experiencesSection}>
          <Text className={styles.sectionLabel}>
            {t("settings.assoc_exp_section_label")} ({experiences.length})
          </Text>

          {renderExperiencesContent()}
        </div>

        <Flex gap={10} className={styles.footer}>
          <Button color="primary" variant="outlined" onClick={onCancel} disabled={isConfirming}>
            {t("common.cancel")}
          </Button>
          <Button
            type="primary"
            danger={isDelete}
            color={isDelete ? undefined : "primary"}
            variant="solid"
            onClick={onConfirm}
            loading={isConfirming}
            disabled={isCheckingAssociations}
          >
            {confirmText}
          </Button>
        </Flex>
      </div>
    </Modal>
  )
}

AssociatedExperiencesConfirmModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  actionType: PropTypes.oneOf([ACTION_TYPES.DEACTIVATE, ACTION_TYPES.DELETE]).isRequired,
  experiences: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      status: PropTypes.string,
      location: PropTypes.string
    })
  ),
  isCheckingAssociations: PropTypes.bool,
  isConfirming: PropTypes.bool,
  customFeeTitle: PropTypes.string
}

export { ACTION_TYPES }
export default AssociatedExperiencesConfirmModal
